import {
  DealInterestLevel,
  DealListFilterOption,
  Deals,
} from "../../models/deals.model";
import { isRentPreferenceRow } from "../../utils/rent-budget.util";

/**
 * O payload de detalhe ACHATA o imóvel externo em `property`, com
 * `isExternal: true` — nunca devolve uma chave `externalProperty`. Testar essa
 * chave dá sempre falso e faz um negócio externo comportar-se como interno.
 *
 * Aceita `deal.externalProperty` na mesma para o caso de o objeto vir de outra
 * fonte (o painel antigo constrói-o assim).
 */
export function isExternalDeal(deal: Deals | any): boolean {
  return !!(deal?.externalProperty || deal?.property?.isExternal === true);
}

/** Id do ExternalProperty num negócio externo. */
export function getExternalPropertyId(deal: Deals | any): any {
  if (!isExternalDeal(deal)) {
    return null;
  }
  const external: any = deal?.externalProperty;
  const property: any = deal?.property;
  return external?.id ?? property?.externalPropertyId ?? property?.id ?? null;
}

export interface DealLocationOptions {
  districts: DealListFilterOption[];
  countiesByDistrict: Map<number, DealListFilterOption[]>;
  parishesByCounty: Map<number, DealListFilterOption[]>;
}

function optionLabel(option: any): string {
  return `${option?.name ?? option?.description ?? ""}`.trim();
}

/** Mesma regra de ListComponent.isMeaningfulLocationName: descarta "-" e vazios. */
function isMeaningfulName(name: any): boolean {
  const rawValue = `${name ?? ""}`.trim();
  if (!rawValue) {
    return false;
  }
  return !/^-+$/.test(rawValue.replace(/\s+/g, ""));
}

function dedupeById(
  options: DealListFilterOption[] = [],
): DealListFilterOption[] {
  const mapById = new Map<number | string, DealListFilterOption>();
  options.forEach((option) => {
    if (option?.id) {
      mapById.set(option.id, option);
    }
  });
  return Array.from(mapById.values());
}

/**
 * Achata a árvore de localizações devolvida por getContactsData() em distritos
 * + índices concelho/freguesia. Porta de ListComponent
 * .populateDealsLocationOptionsFromContacts, que é a fonte usada pelo modal de
 * imóvel externo do painel antigo.
 */
export function buildDealLocationOptions(
  locations: any[] = [],
): DealLocationOptions {
  const districts: DealListFilterOption[] = [];
  const countiesByDistrict = new Map<number, DealListFilterOption[]>();
  const parishesByCounty = new Map<number, DealListFilterOption[]>();

  if (!Array.isArray(locations)) {
    return { districts, countiesByDistrict, parishesByCounty };
  }

  locations.forEach((country: any) => {
    const regions = Array.isArray(country?.regions) ? country.regions : [];

    regions.forEach((region: any) => {
      const districtList = Array.isArray(region?.districts)
        ? region.districts
        : [];

      districtList.forEach((district: any) => {
        const districtId = Number(district?.id);
        const districtName = optionLabel(district);

        if (!Number.isFinite(districtId) || !isMeaningfulName(districtName)) {
          return;
        }

        districts.push({ id: districtId, name: districtName });

        const districtCounties: DealListFilterOption[] = [];
        const counties = Array.isArray(district?.counties)
          ? district.counties
          : [];

        counties.forEach((county: any) => {
          const countyId = Number(county?.id);
          const countyName = optionLabel(county);

          if (!Number.isFinite(countyId) || !isMeaningfulName(countyName)) {
            return;
          }

          districtCounties.push({
            id: countyId,
            name: countyName,
            districtId,
          });

          // O payload usa "parishs" nuns sítios e "parishes" noutros.
          const parishList = Array.isArray(county?.parishs)
            ? county.parishs
            : Array.isArray(county?.parishes)
              ? county.parishes
              : [];

          const countyParishes: DealListFilterOption[] = [];
          parishList.forEach((parish: any) => {
            const parishId = Number(parish?.id);
            const parishName = optionLabel(parish);

            if (!Number.isFinite(parishId) || !isMeaningfulName(parishName)) {
              return;
            }

            countyParishes.push({
              id: parishId,
              name: parishName,
              countyId,
              districtId,
            });
          });

          if (countyParishes.length > 0) {
            parishesByCounty.set(countyId, dedupeById(countyParishes));
          }
        });

        if (districtCounties.length > 0) {
          countiesByDistrict.set(districtId, dedupeById(districtCounties));
        }
      });
    });
  });

  return {
    districts: dedupeById(districts),
    countiesByDistrict,
    parishesByCounty,
  };
}

/** Porta de ListComponent.normalizeDealsPropertyTypeOptions. */
export function buildDealPropertyTypeOptions(
  propertyTypesRaw: any[] = [],
): DealListFilterOption[] {
  if (!Array.isArray(propertyTypesRaw)) {
    return [];
  }

  const seen = new Set<number>();
  return propertyTypesRaw.reduce<DealListFilterOption[]>(
    (propertyTypes, propertyType) => {
      const id = Number(propertyType?.id);
      const name = optionLabel(propertyType);

      if (!Number.isFinite(id) || seen.has(id) || !isMeaningfulName(name)) {
        return propertyTypes;
      }

      seen.add(id);
      propertyTypes.push({ id, name });
      return propertyTypes;
    },
    [],
  );
}

/**
 * O imóvel de uma visita chega com a forma crua do Prisma (spread de
 * `visit.property`), enquanto o modal de proposta espera a forma achatada de
 * `deal.property`. Porta de ListComponent.setShowLeadPropertyFromVisit.
 *
 * Devolve o negócio com o imóvel substituído, para se poder criar uma proposta
 * para o imóvel VISITADO sem tocar no estado da store.
 */
export function buildDealForVisitProposal(
  deal: Deals | any,
  visit: any,
): Deals | null {
  const source = visit?.property;
  if (!deal || !source?.id || source?.isExternal === true) {
    return null;
  }

  return {
    ...deal,
    externalProperty: null,
    property: {
      id: source?.id,
      ref: source?.reference ?? source?.ref,
      reference: source?.reference ?? source?.ref,
      price: source?.price,
      symbol: source?.currency?.symbol ?? source?.symbol ?? "€",
      title:
        source?.propertyTranslations?.[0]?.title ?? source?.title ?? "",
      address:
        source?.propertyAddress?.[0]?.address ?? source?.address ?? "",
      energeticCertification:
        source?.energeticCertification?.description ??
        source?.energeticCertification ??
        "",
      businessType: source?.businessType ?? "",
      grossArea: source?.grossArea ?? 0,
      image: source?.propertyFiles?.[0]?.fileName ?? source?.image ?? "",
      yearBuilt: source?.yearBuilt ?? source?.yearOfConstruction ?? "",
      listingAgent:
        // Pela flag e não pelo índice: o select traz agora também o consultor
        // principal, e o primeiro da lista podia ser esse.
        ((source?.propertyAgents ?? []).find(
          (agent: any) => agent?.isListingAgent,
        ) ?? source?.propertyAgents?.[0])?.user?.name ??
        source?.listingAgent ??
        source?.agentName ??
        "",
      store: source?.store?.name ?? source?.store,
      storeData: source?.store ?? source?.storeData,
      propertyType: source?.propertyType ?? source?.propertyType2 ?? "",
      typology: source?.typology ?? "",
      isExternal: false,
    },
  } as Deals;
}

/**
 * Normaliza o imóvel de origem para pré-preencher o formulário de imóvel
 * externo. Aceita tanto um ExternalProperty como o `property` do negócio
 * quando este é externo.
 */
/**
 * Imóveis do separador "Imóveis" de um negócio: os associados ao NEGÓCIO
 * (deals_properties) mais os associados ao CONTACTO como comprador, sem
 * duplicados (a associação ao negócio ganha, porque traz isTarget e ordem).
 *
 * Vive aqui porque a lista e o contador do separador têm de usar o MESMO
 * critério — o contador só contava os do contacto e ficava a zero depois de
 * associar um imóvel ao negócio.
 */
export function collectDealPropertyRows(deal: Deals | any): any[] {
  const dealRows: any[] = Array.isArray((deal as any)?.dealProperties)
    ? (deal as any).dealProperties.filter((item: any) => item?.property)
    : [];

  const contactRows = collectContactBuyerPropertyRows(deal);

  const seen = new Set(
    dealRows.map((row) => `${row?.property?.id ?? ""}`).filter(Boolean),
  );

  return [
    ...dealRows,
    ...contactRows.filter((row) => !seen.has(`${row?.property?.id ?? ""}`)),
  ];
}

/**
 * Imóveis associados ao CONTACTO restritos ao papel de COMPRADOR. A mesma
 * tabela guarda também a relação de proprietário, e sem este filtro um
 * contacto que seja as duas coisas via os seus próprios imóveis listados
 * como associados ao negócio.
 *
 * `contactType` tem default BUYER no schema, mas registos antigos podem vir
 * sem valor — nesse caso mantêm-se, para não esconder dados existentes.
 */
function collectContactBuyerPropertyRows(deal: Deals | any): any[] {
  const items = deal?.contact?.contactsCostumersProperties;
  if (!Array.isArray(items)) {
    return [];
  }

  const contactId = `${deal?.contact?.id ?? ""}`;

  return items
    .filter((item: any) => {
      if (!item?.property) {
        return false;
      }

      const role = `${item?.contactType ?? ""}`.toUpperCase();
      if (role !== "" && role !== "BUYER") {
        return false;
      }

      // Imóveis do próprio contacto não são opções de compra para ele. O
      // contactType da linha não chega: um contacto pode ter a linha como
      // BUYER e continuar a constar como proprietário do imóvel.
      const owners = item?.property?.contactsCostumersProperties;
      if (Array.isArray(owners) && contactId) {
        const isOwner = owners.some(
          (owner: any) => `${owner?.contactId ?? ""}` === contactId,
        );
        if (isOwner) {
          return false;
        }
      }

      return true;
    })
    .map((item: any) => ({ ...item, origin: "contact" }));
}

export function buildExternalPropertyFormSource(source: any): any {
  if (!source) {
    return {};
  }

  const location = source?.location ?? {};
  return {
    reference: source?.reference ?? source?.ref ?? "",
    propertyTypeId:
      source?.propertyTypeGroupId ??
      source?.propertyTypeId ??
      source?.propertyType?.id ??
      null,
    agency: source?.agency ?? "",
    typology: source?.typology ?? "",
    address: source?.address ?? location?.address ?? "",
    zipCode: source?.zipCode ?? location?.zipCode ?? "",
    price: source?.price ?? null,
    districtId: source?.districtId ?? location?.district?.id ?? null,
    countyId: source?.countyId ?? location?.county?.id ?? null,
    parishId: source?.parishId ?? location?.parish?.id ?? null,
    agentId: source?.agentId ?? source?.agent?.id ?? null,
    link: source?.link ?? "",
  };
}

/**
 * Link do anúncio de um imóvel externo: http(s) opcional, sem espaços, com
 * pelo menos um ponto no domínio. O servidor completa o esquema em falta.
 */
export const EXTERNAL_PROPERTY_LINK_PATTERN =
  /^(https?:\/\/)?[^\s/?#]+\.[^\s/?#]+(?:[/?#]\S*)?$/i;

/**
 * Mesma semântica de ListComponent.resolveDealInterestLevel: o nível vem do
 * interestLevel da preferência do contacto associada ao imóvel do negócio
 * (ou da primeira preferência), com thresholds >66 = hot e >33 = warm.
 */
export function getDealContactPreferences(deal: Deals | any): any[] {
  const preferences =
    deal?.contact?.contactsPreferences ??
    deal?.contact?.preferences ??
    deal?.lead?.contact?.contactsPreferences ??
    deal?.lead?.contact?.preferences;

  if (Array.isArray(preferences)) {
    return preferences.filter(
      (preference) => preference && typeof preference === "object",
    );
  }

  if (preferences && typeof preferences === "object") {
    return [preferences];
  }

  return [];
}

/**
 * A preferência de onde sai o nível de interesse: a que está ligada ao imóvel
 * do negócio, senão a primeira. É a mesma escolha de `resolveDealInterestLevel`
 * — só que devolve a preferência, para se poder gravar nela.
 */
export function resolveDealInterestPreference(deal: Deals | any): any | null {
  const preferences = getDealContactPreferences(deal);
  if (preferences.length === 0) {
    return null;
  }

  const propertyId = `${deal?.property?.id ?? ""}`.trim();
  const preferenceForProperty =
    propertyId.length > 0
      ? preferences.find((preference) => {
        const relatedProperties = Array.isArray(
          preference?.contactPreferencesProperties,
        )
          ? preference.contactPreferencesProperties
          : [];

        return relatedProperties.some(
          (relation: any) => `${relation?.propertyId ?? ""}` === propertyId,
        );
      })
      : null;

  return preferenceForProperty ?? preferences[0];
}

export function resolveDealInterestValue(deal: Deals | any): number | null {
  const value = Number(resolveDealInterestPreference(deal)?.interestLevel);
  return Number.isFinite(value) ? value : null;
}

export function resolveDealInterestLevel(
  deal: Deals | any,
): DealInterestLevel | null {
  const selectedPreference = resolveDealInterestPreference(deal);
  if (!selectedPreference) {
    return null;
  }

  const interestLevel = Number(selectedPreference?.interestLevel);
  if (!Number.isFinite(interestLevel)) {
    return null;
  }

  if (interestLevel > 66) {
    return "hot";
  }
  if (interestLevel > 33) {
    return "warm";
  }
  return "cold";
}

export function getDealInterestIconClass(
  level: DealInterestLevel | null,
): string {
  if (level === "hot") {
    return "ri-fire-fill";
  }
  if (level === "warm") {
    return "mdi mdi-thermometer-chevron-up";
  }
  return "mdi mdi-snowflake";
}

/** Mesmo formato de preço usado no board (espaços de milhar + símbolo). */
export function priceFormat(propertyPrice: any, symbol: string = "€") {
  if (propertyPrice) {
    let price = propertyPrice.toString().split(".");
    const spaces = (value: any) =>
      String(value).replace(/(?!^)(?=(?:\d{3})+$)/g, " ");

    const price1 = spaces(price[0]);
    if (price.length > 1) return price1 + "," + price[1] + " " + symbol;

    return price1 + " " + symbol;
  }

  return propertyPrice;
}

/**
 * Montante com cêntimos: nunca arredonda ao euro. Inteiros saem sem casas
 * decimais ("42 000 €"); o resto sai sempre com duas ("3,60 €", "36,88 €"),
 * porque priceFormat cola a parte decimal tal como vem e "3.6" ficaria "3,6 €".
 */
export function priceFormatCents(value: any, symbol: string = "€"): string | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const cents = Math.round(parsed * 100) / 100;
  if (cents === 0) return `0 ${symbol}`;
  if (Number.isInteger(cents)) return priceFormat(cents, symbol);
  return priceFormat(cents.toFixed(2), symbol);
}

function parseDealBudgetValue(value: any): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  const normalized = `${value ?? ""}`
    .trim()
    .replace(/\s/g, "")
    .replace(/[^\d,.-]/g, "");

  if (!normalized) {
    return null;
  }

  let standardized = normalized;
  const lastComma = standardized.lastIndexOf(",");
  const lastDot = standardized.lastIndexOf(".");

  if (lastComma > -1 && lastDot > -1) {
    standardized =
      lastComma > lastDot
        ? standardized.replace(/\./g, "").replace(",", ".")
        : standardized.replace(/,/g, "");
  } else if (lastComma > -1) {
    standardized = standardized.replace(",", ".");
  }

  const parsed = Number(standardized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function findPreferenceForDealProperty(
  preferences: any[],
  propertyId: string,
): any | null {
  if (!propertyId) {
    return null;
  }

  return (
    preferences.find((preference) => {
      const relatedProperties = Array.isArray(
        preference?.contactPreferencesProperties,
      )
        ? preference.contactPreferencesProperties
        : [];

      return relatedProperties.some(
        (relation: any) => `${relation?.propertyId ?? ""}` === propertyId,
      );
    }) ?? null
  );
}

function resolveDealBudgetValues(
  deal: Deals | any,
  purpose?: "sale" | "rent",
): { min: number | null; max: number | null } | null {
  const allPreferences = getDealContactPreferences(deal);
  if (allPreferences.length === 0) {
    return null;
  }

  // Com finalidade pedida, só contam as linhas dela: a renda mensal vive nas
  // linhas de arrendamento e o orçamento de compra nas outras. Se nenhuma
  // linha for dessa finalidade fica-se com todas, como antes.
  const ofPurpose = purpose
    ? allPreferences.filter(
        (preference: any) =>
          isRentPreferenceRow(preference) === (purpose === "rent"),
      )
    : allPreferences;
  const preferences = ofPurpose.length > 0 ? ofPurpose : allPreferences;

  const propertyId = `${deal?.property?.id ?? ""}`.trim();
  const selectedPreference = findPreferenceForDealProperty(
    preferences,
    propertyId,
  );

  if (selectedPreference) {
    const selectedMin = parseDealBudgetValue(selectedPreference?.budgetMin);
    const selectedMax = parseDealBudgetValue(selectedPreference?.budgetMax);
    if (selectedMin || selectedMax) {
      return { min: selectedMin, max: selectedMax };
    }
  }

  const minValues = preferences
    .map((preference) => parseDealBudgetValue(preference?.budgetMin))
    .filter((value): value is number => value !== null);
  const maxValues = preferences
    .map((preference) => parseDealBudgetValue(preference?.budgetMax))
    .filter((value): value is number => value !== null);

  const min = minValues.length > 0 ? Math.min(...minValues) : null;
  const max = maxValues.length > 0 ? Math.max(...maxValues) : null;

  if (!min && !max) {
    return null;
  }

  if (min && max && min > max) {
    return { min: max, max: min };
  }

  return { min, max };
}

/** Mesmo formato de datas do painel antigo (por idioma). */
export function getDateFormatForLang(lang: string, hour = false): string {
  const dateF = lang === "pt" ? "DD/MM/YYYY" : "YYYY/MM/DD";
  return hour ? `${dateF} HH:mm` : dateF;
}

/**
 * Escolhe a unidade em função do tamanho. getSizeInMb dividia sempre por
 * 1024², pelo que um ficheiro de 340 KB aparecia como "0.33MB".
 */
export function formatFileSize(size: any): string {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Chave i18n do tipo de documento (mesma semântica de ListComponent.getFileType). */
export function getFileTypeKey(type: string): string {
  switch (type) {
    case "MEDIATION":
      return "t-m-properties.t-mediation-agreement";
    // O CMI devolvido assinado pelo proprietário. Faltava aqui: caía no
    // `default:` e aparecia rotulado "Outro", indistinguível de um ficheiro
    // qualquer — daí a sensação de não se saber para onde ia o documento.
    case "MEDIATION_SIGNED":
      return "t-cmi-signed-document";
    case "PROPOSAL":
      return "t-deal-actions.t-proposal-btn";
    case "VISIT_FORM":
      return "t-deal-actions.t-visit-form";
    case "MANUAL":
      return "t-deal-actions.t-manual-doc";
    default:
      return "t-mediation.t-other";
  }
}

/** Grupos do separador de documentos, por ordem de apresentação. */
export type DealDocumentGroupKey =
  | "cmi"
  | "visit"
  | "proposal"
  | "property"
  | "other";

export interface DealDocumentGroup {
  key: DealDocumentGroupKey;
  labelKey: string;
  icon: string;
  files: any[];
}

const DOCUMENT_GROUP_BY_TYPE: Record<string, DealDocumentGroupKey> = {
  MEDIATION: "cmi",
  MEDIATION_SIGNED: "cmi",
  VISIT_FORM: "visit",
  PROPOSAL: "proposal",
};

const DOCUMENT_GROUP_META: Array<{
  key: DealDocumentGroupKey;
  labelKey: string;
  icon: string;
}> = [
  { key: "cmi", labelKey: "t-listing-detail-tab-cmi", icon: "ri-file-text-line" },
  {
    key: "visit",
    labelKey: "t-deal-actions.t-visit-form",
    icon: "ri-calendar-check-line",
  },
  {
    key: "proposal",
    labelKey: "t-deal-actions.t-proposal-btn",
    icon: "ri-hand-coin-line",
  },
  {
    key: "property",
    labelKey: "t-deals-detail-property-documents",
    icon: "ri-home-4-line",
  },
  { key: "other", labelKey: "t-mediation.t-other", icon: "ri-folder-line" },
];

/**
 * Agrupa os documentos por natureza.
 *
 * Uma lista plana punha o CMI gerado, o CMI assinado pelo proprietário, as
 * fichas de visita e um PDF qualquer carregado à mão todos ao mesmo nível —
 * e o assinado nem sequer tinha rótulo próprio. Grupos vazios não aparecem.
 */
export function groupDealDocuments(
  files: any[],
  propertyFiles: any[] = [],
): DealDocumentGroup[] {
  const buckets = new Map<DealDocumentGroupKey, any[]>();

  for (const file of files ?? []) {
    const key =
      DOCUMENT_GROUP_BY_TYPE[`${file?.type ?? ""}`.toUpperCase()] ?? "other";
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(file);
  }

  if (propertyFiles?.length) {
    buckets.set("property", [...propertyFiles]);
  }

  return DOCUMENT_GROUP_META.filter((group) => buckets.get(group.key)?.length).map(
    (group) => ({ ...group, files: buckets.get(group.key)! }),
  );
}

export function generateProposalRef(id: any): string {
  if (id) {
    const ref = id.toString().padStart(4, "0");
    return `#P${ref}`;
  }
  return "";
}

/** Badge por ícone de estado de proposta (mesma semântica de ListComponent.getBadge). */
export function getProposalBadgeClass(icon: any): string {
  if (icon) {
    switch (icon) {
      case "ri-close-circle-line":
        return "badge-soft-danger";
      case "ri-check-double-line":
        return "badge-soft-success";
      case "ri-trophy-line":
        return "badge-soft-gold";
      default:
        return "badge-soft-info";
    }
  }
  return "badge-soft-info";
}

/** Estado agregado de uma proposta interna, para tiles/cartões/progresso. */
export type ProposalCategory = "pending" | "accepted" | "declined";

/**
 * Categoria de uma linha de proposta do payload (`deal.proposals[i]`).
 *
 * Decide pelo ícone do último passo do histórico (mesma semântica do board
 * antigo): recusada, aceite (check duplo ou troféu) ou pendente. Extraído da
 * `proposals-tab` para o checklist de progresso da venda usar a mesma regra
 * — duas implementações a responder "esta proposta foi aceite?" divergiam.
 */
export function resolveProposalCategory(row: any): ProposalCategory {
  const latest = row?.proposal?.lastAction ?? row?.proposal?.firstProposal;
  // Reserva, CPCV (Sale Agreement) e venda concluída são etapas DEPOIS da
  // aceitação — a proposta está aceite mesmo que o ícone do passo não seja o
  // de "aceite".
  const status = String(
    latest?.proposalStatusCode ?? latest?.originalStatus ?? "",
  )
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (
    status === "accepted" ||
    status === "cpcv" ||
    status === "sale agreement" ||
    status === "sale completed" ||
    status.includes("reserv")
  ) {
    return "accepted";
  }
  switch (getProposalBadgeClass(latest?.icon)) {
    case "badge-soft-danger":
      return "declined";
    case "badge-soft-success":
    case "badge-soft-gold":
      return "accepted";
    default:
      return "pending";
  }
}

/**
 * Proposta em aberto de `deal.proposals` para um imóvel — a que ainda bloqueia
 * uma nova. O servidor aplica a mesma regra em `POST /proposals`; aqui só se
 * evita preencher o modal para nada. Em aberto = tudo o que não é recusada
 * nem venda concluída: pendente, contraproposta, aceite, reserva e CPCV.
 */
export function findOpenProposalForProperty(
  deal: any,
  propertyId: any,
): any | null {
  if (!propertyId || !Array.isArray(deal?.proposals)) {
    return null;
  }
  const wanted = String(propertyId);
  return (
    deal.proposals.find((row: any) => {
      if (String(row?.property?.id ?? "") !== wanted) {
        return false;
      }
      if (resolveProposalCategory(row) === "declined") {
        return false;
      }
      const latest = row?.proposal?.lastAction ?? row?.proposal?.firstProposal;
      const status = String(
        latest?.proposalStatusCode ?? latest?.originalStatus ?? "",
      )
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
      return status !== "sale completed";
    }) ?? null
  );
}

/**
 * Chave i18n de override do estado da proposta (reserved/CPCV) — mesma
 * semântica de ListComponent.getProposalStatusTranslationKey. Devolve null
 * quando o texto traduzido do próprio estado deve ser usado tal-e-qual.
 */
export function getProposalStatusTranslationKey(
  status: any,
  isRent = false,
): string | null {
  const normalizedStatus = String(
    status?.proposalStatusCode ??
    status?.originalStatus ??
    status?.proposalStatus ??
    "",
  )
    .trim()
    .toLowerCase();

  if (!normalizedStatus) {
    return null;
  }

  if (normalizedStatus === "reserved" || normalizedStatus.includes("reserv")) {
    return "t-proposals.t-status-reserved";
  }

  if (
    normalizedStatus === "sale agreement" ||
    normalizedStatus.replace(/\s+/g, "") === "saleagreement" ||
    normalizedStatus === "cpcv"
  ) {
    return isRent
      ? "t-proposals.t-status-lease-agreement"
      : "t-proposals.t-status-cpcv";
  }

  return null;
}

/** Mesma semântica de ListComponent.getMoveTitle. */
export function getMoveTitleKey(title: any): string {
  switch (title) {
    case "AutoWon":
      return "t-leads-m.t-moved-to-won";
    case "AutoLost":
      return "t-leads-m.t-moved-to-lost";
    case "AutoUnmarkWon":
      return "t-leads-m.t-moved-unmark-won";
    case "AutoUnmarkLost":
      return "t-leads-m.t-moved-unmark-lost";
    case "AutoDealConverted":
      return "t-leads-m.t-moved-deal-converted";
    case "ReturnedToSalesPipeline":
      return "t-deals-process-returned-to-sales-history";
    // Visitas (agendada/realizada/cancelada) — antes só o arrastar no funil
    // ficava no histórico.
    case "VisitScheduled":
      return "t-deals-history-visit-scheduled";
    case "VisitDone":
      return "t-deals-history-visit-done";
    case "VisitUndone":
      return "t-deals-history-visit-undone";
    case "VisitCanceled":
      return "t-deals-history-visit-canceled";
    // Marcos da angariação. Antes destes, o histórico de uma angariação só
    // registava mudanças de etapa e o fecho — nada do trabalho pelo meio.
    case "AutoPropertyFillStarted":
      return "t-listing-history-property-fill-started";
    case "AutoPropertyFieldChanged":
      return "t-listing-history-property-changed";
    case "AutoSentToValidation":
      return "t-listing-history-sent-to-validation";
    case "AutoDefinitiveReference":
      return "t-listing-history-definitive-reference";
    case "AutoCmiGenerated":
      return "t-listing-history-cmi-generated";
    case "AutoCmiSigned":
      return "t-listing-history-cmi-signed";
    case "AutoValuationSaved":
      return "t-listing-history-valuation-saved";
    // Portal do proprietário
    case "AutoOwnerPortalSent":
      return "t-listing-history-owner-portal-sent";
    case "AutoOwnerDocumentSubmitted":
      return "t-listing-history-owner-document-submitted";
    case "AutoOwnerDocumentApproved":
      return "t-listing-history-owner-document-approved";
    case "AutoOwnerDocumentRejected":
      return "t-listing-history-owner-document-rejected";
    default:
      return "t-leads-m.t-moved-to";
  }
}

/** Uma alteração de campo registada no histórico: rótulo, antes e depois. */
export interface HistoryChangeRow {
  label: string;
  before: string;
  after: string;
}

/**
 * Alterações de campo de uma entrada de histórico.
 *
 * Vêm da coluna `metadata` (`{ changes: [{ label, before, after }] }`),
 * escrita a partir do mesmo `buildPropertyLogChanges` que alimenta o registo
 * de actividade. O `label` já é uma chave i18n.
 */
export function getHistoryChanges(item: any): HistoryChangeRow[] {
  const changes = item?.metadata?.changes;
  if (!Array.isArray(changes)) return [];

  return changes
    .filter((change: any) => change?.label)
    .map((change: any) => ({
      label: `${change.label}`,
      before: `${change.before ?? ""}`.trim() || "—",
      after: `${change.after ?? ""}`.trim() || "—",
    }));
}

export function isVisitOverdue(visit: any): boolean {
  if (visit?.isVisited) {
    return false;
  }
  const startAt = visit?.startAt ? new Date(visit.startAt).getTime() : null;
  if (!startAt) {
    return false;
  }
  return startAt < Date.now();
}

export function getVisitOutcomeBadgeClass(visit: any): string {
  if (visit?.isVisited) {
    return "visit-status-done";
  }
  if (isVisitOverdue(visit)) {
    return "visit-status-overdue";
  }
  return "visit-status-scheduled";
}

export function getVisitOutcomeLabelKey(visit: any): string {
  if (visit?.isVisited) {
    return "t-deals-visit-status-completed";
  }
  if (isVisitOverdue(visit)) {
    return "t-deals-visit-status-overdue";
  }
  return "t-deals-visit-status-scheduled";
}

/** Mesma semântica de ListComponent.getDealBudgetLabel. */
export function getDealBudgetLabel(
  deal: Deals | any,
  purpose?: "sale" | "rent",
): string | null {
  const budget = resolveDealBudgetValues(deal, purpose);
  if (!budget) {
    return null;
  }

  const currencySymbol = deal?.property?.symbol;

  if (budget.min && budget.max) {
    return `${priceFormat(budget.min, currencySymbol)} - ${priceFormat(budget.max, currencySymbol)}`;
  }

  if (budget.min) {
    return `>= ${priceFormat(budget.min, currencySymbol)}`;
  }

  if (budget.max) {
    return `<= ${priceFormat(budget.max, currencySymbol)}`;
  }

  return null;
}

/** Mesma semântica de ListComponent.buildAggregatedDealContactPreferences. */
export function buildAggregatedDealContactPreferences(
  preferences: any[] = [],
): any {
  if (!Array.isArray(preferences) || preferences.length === 0) {
    return undefined;
  }

  const firstPreference = preferences[0] ?? {};
  const uniquePositiveIds = (values: any[]) =>
    values
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
      .filter((value, index, array) => array.indexOf(value) === index);
  const firstPositiveId = (values: any[]) => uniquePositiveIds(values)[0];
  const interestLevels = preferences
    .map((preference) => Number(preference?.interestLevel))
    .filter((value) => Number.isFinite(value) && value > 0);

  return {
    ...firstPreference,
    countryId:
      firstPositiveId(preferences.map((preference) => preference?.countryId)) ??
      firstPreference?.countryId,
    regionId:
      firstPositiveId(preferences.map((preference) => preference?.regionId)) ??
      firstPreference?.regionId,
    businessTypeIds: uniquePositiveIds(
      preferences.map((preference) => preference?.businessTypeId),
    ),
    propertyTypeGroupIds: uniquePositiveIds(
      preferences.map((preference) => preference?.propertyTypeGroupId),
    ),
    propertyConditionIds: uniquePositiveIds(
      preferences.map((preference) => preference?.propertyConditionId),
    ),
    districtIds: uniquePositiveIds(
      preferences.map((preference) => preference?.districtId),
    ),
    countyIds: uniquePositiveIds(
      preferences.map((preference) => preference?.countyId),
    ),
    parishIds: uniquePositiveIds(
      preferences.map((preference) => preference?.parishId),
    ),
    interestLevel:
      interestLevels.length > 0
        ? Math.max(...interestLevels)
        : firstPreference?.interestLevel,
  };
}

/** Mesma montagem do contacto de ListComponent.editContactModal. */
export function buildDealContactForEdit(deal: Deals | any): any | null {
  const contactData: any = deal?.contact ?? {};
  if (!contactData?.id) {
    return null;
  }

  let hasDeal = false;
  if (Array.isArray(contactData?.lead) && contactData.lead.length > 0) {
    contactData.lead.forEach((lead: any) => {
      if (lead?.deal && lead.deal?.id) {
        hasDeal = true;
      }
    });
  }

  const normalizedContactSourceId = Number(
    contactData?.contactSourceId ??
      contactData?.contactSource?.id ??
      contactData?.contactSource ??
      0,
  );

  return {
    ...contactData,
    id: contactData?.id,
    firstName: contactData?.firstName,
    lastName: contactData?.lastName,
    email: contactData?.email,
    status: contactData?.status,
    contactSource:
      Number.isFinite(normalizedContactSourceId) && normalizedContactSourceId > 0
        ? normalizedContactSourceId
        : null,
    contactSourceDetails: contactData?.contactSource,
    contactType: contactData?.contactTypeId,
    user: contactData?.userId,
    phoneNumber: contactData?.phoneNumber,
    phoneNumber2: contactData?.phoneNumber2,
    address: contactData?.address,
    zipCode: contactData?.zipCode,
    obs: contactData?.obs,
    hasFile: contactData?.hasFile,
    dataConsent: contactData?.dataConsent,
    hasDeal,
    file:
      contactData?.contactFiles && contactData?.contactFiles?.length > 0
        ? {
            name: contactData?.contactFiles[0].originalName,
            size: contactData?.contactFiles[0].size,
            path: contactData?.contactFiles[0]?.fileName,
          }
        : undefined,
    preferences: buildAggregatedDealContactPreferences(
      getDealContactPreferences(deal),
    ),
    types: contactData?.contactsContactType,
    additionalContacts: contactData?.additionalContact,
    contactUser: contactData?.contactUser,
  };
}
